import { tt } from "../i18n/tt";
import { lazyI18n } from "../i18n/lazyText";
// 약관·정책 동의 상태 판정 규칙 (순수 함수 — IO 없음)
// 기획: docs/ops/policy-consent-plan.md §6 동작 규칙
//
// 이력 규약: user_policy_consent 는 append-only 이며 현재 상태는
// (user_email, policy_key) 의 "가장 최근 행"으로 판정한다.

// 철회·유예 마감 — 정책 전문 제7조(유예·철회). KST 기준 2026-09-30 이 지나면 마감.
export const GRACE_END_EXCLUSIVE = new Date("2026-10-01T00:00:00+09:00");

export function isWithinGrace(now = new Date()) {
  return now.getTime() < GRACE_END_EXCLUSIVE.getTime();
}

// 동의 철회는 유예 종료(9/30)까지만 가능 — 제7조 6항
export function canWithdraw(now = new Date()) {
  return isWithinGrace(now);
}

// 어드민 임퍼소네이션 여부 — 대리 동의가 성립하면 이력의 증거력이 무너지므로
// 이 상태에서는 동의·거절·철회 액션을 막는다 (기획 §6-6).
export function isImpersonating() {
  try {
    const isInIframe = typeof window !== "undefined" && window.self !== window.top;
    const key = isInIframe ? "adminViewImpersonateEmail" : "impersonateEmail";
    return Boolean(window.localStorage.getItem(key));
  } catch (e) {
    return false;
  }
}

// 동의 팝업 노출 대상 판정 (2026-08-31 대표 확정)
//
// 크리에이터 **전원**에게 띄운다 — 지원/자력 판정 여부와 무관하다.
// 판정(user.shortform_eligibility)은 "정책이 실제로 적용되는가"(정산)를 정하는 값이지,
// "동의를 받을 것인가"를 정하는 값이 아니다.
//
// 이전에는 shortform_eligibility='supported' 인 **채널**을 가진 사람만 대상이었는데,
// 채널 등록이 크리에이터의 자발적 행위라 네이버·카카오를 실제로 운영해도 등록하지
// 않으면 대상에서 통째로 빠졌다(크리에이터 157명 중 127명이 보이지 않았다).
//
// role 은 "Admin , Master , Creator , Copyrights Holder , Manager" 처럼 여러 값이
// 한 문자열에 들어온다 — 반드시 부분 일치로 본다(=== 비교는 겸직 계정을 놓친다).
export function isConsentTarget(role) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  if (r.includes("admin") || r.includes("platform")) return false;
  return r.includes("creator");
}

// rows: 해당 사용자의 user_policy_consent 행들 (모든 정책 섞여 있어도 됨)
// 반환: { [policy_key]: 최신 행 }
export function latestConsentByPolicy(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    if (!row || !row.policy_key) return;
    const prev = map[row.policy_key];
    if (!prev || new Date(row.created_at) > new Date(prev.created_at)) {
      map[row.policy_key] = row;
    }
  });
  return map;
}

// 정책 하나에 대한 현재 동의 상태
//   agreed         — 현재 버전에 동의
//   agreed_legacy  — 이관(legacy) 동의 (가입 시 동의로 표기 — 기획 §5)
//   needs_reconsent— 과거 버전에 동의했으나 정책이 개정됨
//   declined       — 거절
//   withdrawn      — 철회 (거절과 동일 처리 — 제7조 6항)
//   none           — 기록 없음
export function consentStateFor(policy, latestRow) {
  if (!latestRow) return "none";
  if (!latestRow.agreed) {
    return latestRow.method === "withdrawal" ? "withdrawn" : "declined";
  }
  if (latestRow.version === policy.version) return "agreed";
  if (latestRow.version === "legacy") return "agreed_legacy";
  return "needs_reconsent";
}

export const CONSENT_STATE_LABEL = lazyI18n(() => ({
  agreed: tt("a.a47e2a04", "동의 완료"),
  agreed_legacy: tt("a.a47e2a04", "동의 완료"),
  needs_reconsent: tt("a.60e98079", "확인 필요"),
  declined: tt("a.8feba42e", "거절"),
  withdrawn: tt("a.20834c46", "철회"),
  none: tt("a.fbaf9f99", "미동의"),
}));

// 차단형 팝업 노출 여부 — is_active 필수 정책 중 현재 버전 동의가 없을 때.
// 거절·철회·유보는 "다음 화면 진입 시 다시 노출" (기획 §6-1).
// legacy 이관 동의는 재동의 요청(버전 개정) 전까지 유효한 것으로 본다.
export function needsPrompt(policy, state) {
  if (!policy || !policy.is_active || !policy.is_required) return false;
  return state !== "agreed" && state !== "agreed_legacy";
}

// 설정 화면의 "확인 필요" 배지 건수 — 필수 정책 중 현재 버전 동의가 없는 것
export function pendingRequiredCount(policies, latestByKey) {
  return (policies || []).filter((p) => {
    if (!p.is_active || !p.is_required) return false;
    const state = consentStateFor(p, latestByKey[p.key]);
    // legacy 동의는 재동의 요청 전까지 유효한 것으로 본다 (이용약관·개인정보)
    return state !== "agreed" && state !== "agreed_legacy";
  }).length;
}

// 문안 전문의 SHA-256 (hex). Web Crypto 미지원 환경에서는 null — document_hash 는
// null 허용이며, 이 경우 버전 문자열만으로 기록된다.
export async function hashDocumentText(text) {
  try {
    if (typeof crypto === "undefined" || !crypto.subtle) return null;
    const data = new TextEncoder().encode(text);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch (e) {
    return null;
  }
}
